import type { AllocationPatch } from './api-parity';
import type { NoteClient, NoteProject } from './types';

/** The note's projects in the form (#915 C3): 0–3, each with its share as typed («33,34»). */
export type ProjectShare = {
  readonly projectId: string;
  readonly code: string;
  readonly name: string;
  readonly percent: string;
};
export type ChosenClient = Pick<NoteClient, 'clientId' | 'code' | 'name'>;
export type Allocation = {
  readonly projects: readonly ProjectShare[];
  readonly client: ChosenClient | null;
};

export const MAX_PROJECTS = 3;
const WHOLE = 10_000;

export function emptyAllocation(): Allocation {
  return { projects: [], client: null };
}

/** The allocation a saved note holds, with its shares in Italian («50,00»). */
export function allocationOf(
  projects: readonly NoteProject[] | undefined,
  client: NoteClient | null | undefined,
): Allocation {
  return {
    projects: (projects ?? []).map((project) => ({
      projectId: project.projectId,
      code: project.code,
      name: project.name,
      percent: formatBp(toBp(project.percent) ?? 0),
    })),
    client: client ? { clientId: client.clientId, code: client.code, name: client.name } : null,
  };
}

/** Basis points from «33,33» or «33.33»: two decimals at most, more than 0 and at most 100. */
export function toBp(text: string): number | null {
  const match = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(text.trim());
  if (!match) return null;
  const [, whole = '', decimals = ''] = match;
  const bp = Number(whole) * 100 + Number(decimals.padEnd(2, '0'));
  return bp > 0 && bp <= WHOLE ? bp : null;
}

/** «33,34» from 3334. */
export function formatBp(bp: number): string {
  return `${Math.floor(bp / 100)},${String(bp % 100).padStart(2, '0')}`;
}

/** Equal shares, the first ones taking the leftover basis point: 33,34 / 33,33 / 33,33. */
export function splitEqually(projects: readonly ProjectShare[]): ProjectShare[] {
  const count = projects.length;
  if (count === 0) return [];
  const base = Math.floor(WHOLE / count);
  const rest = WHOLE - base * count;
  return projects.map((project, index) => ({
    ...project,
    percent: formatBp(base + (index < rest ? 1 : 0)),
  }));
}

export type SharesState =
  | { readonly kind: 'none' }
  | { readonly kind: 'ok'; readonly totalBp: number }
  | { readonly kind: 'unchosen' }
  | { readonly kind: 'format' }
  | { readonly kind: 'sum'; readonly totalBp: number; readonly missingBp: number };

/** Whether the shares may be saved, and what to tell the user when they may not. */
export function sharesState(projects: readonly ProjectShare[]): SharesState {
  if (projects.length === 0) return { kind: 'none' };
  if (projects.some((project) => project.projectId === '')) return { kind: 'unchosen' };
  // One project: the share is a fixed 100 %, whatever the field held before.
  if (projects.length === 1) return { kind: 'ok', totalBp: WHOLE };
  const shares = projects.map((project) => toBp(project.percent));
  if (shares.some((share) => share === null)) return { kind: 'format' };
  const totalBp = shares.reduce<number>((sum, share) => sum + (share ?? 0), 0);
  return totalBp === WHOLE
    ? { kind: 'ok', totalBp }
    : { kind: 'sum', totalBp, missingBp: WHOLE - totalBp };
}

export function allocationSavable(allocation: Allocation): boolean {
  const state = sharesState(allocation.projects);
  return state.kind === 'none' || state.kind === 'ok';
}

/** The server's form: one project is 100, the shares with a dot («33.34»). */
export function toAllocationPatch(allocation: Allocation): AllocationPatch {
  const single = allocation.projects.length === 1;
  return {
    projects: allocation.projects.map((project) => ({
      projectId: project.projectId,
      percent: single ? '100' : formatBp(toBp(project.percent) ?? 0).replace(',', '.'),
    })),
    clientId: allocation.client?.clientId ?? null,
  };
}

/** True when the form's allocation differs from the note's, so a PATCH is worth sending. */
export function allocationChanged(before: Allocation, after: Allocation): boolean {
  return JSON.stringify(toAllocationPatch(before)) !== JSON.stringify(toAllocationPatch(after));
}
